import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { 
  Bell, 
  Mail, 
  Shield, 
  Search,
  Filter,
  Settings,
  Check,
  X,
  AlertTriangle,
  Info,
  CheckCircle,
  Clock,
  Trash2,
  CheckCheck,
  Send,
  Loader2
} from "lucide-react";
import { Link } from "wouter";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";

export default function Notifications() {
  const [searchTerm, setSearchTerm] = useState("");
  const [filterType, setFilterType] = useState("all");
  const [showUnreadOnly, setShowUnreadOnly] = useState(false);
  const [isReportDialogOpen, setIsReportDialogOpen] = useState(false);
  const [reportForm, setReportForm] = useState({
    email: "",
    reportType: "security-summary",
    includeScans: true,
    includeAlerts: true,
    includeRecommendations: true
  });
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data: notificationsData, isLoading } = useQuery({
    queryKey: ["/api/notifications"],
    refetchInterval: 30000,
  });

  const markAsReadMutation = useMutation({
    mutationFn: async (notificationId: string) => {
      return apiRequest(`/api/notifications/${notificationId}/read`, "PUT");
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/notifications"] });
      toast({ title: "Notification marked as read" });
    },
  });

  const deleteNotificationMutation = useMutation({
    mutationFn: async (notificationId: string) => {
      return apiRequest(`/api/notifications/${notificationId}`, "DELETE");
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/notifications"] });
      toast({ title: "Notification deleted" });
    },
  });

  const markAllAsReadMutation = useMutation({
    mutationFn: async () => {
      return apiRequest("/api/notifications/mark-all-read", "PUT");
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/notifications"] });
      toast({ title: "All notifications marked as read" });
    },
  });

  const sendReportMutation = useMutation({
    mutationFn: async (reportData: typeof reportForm) => {
      const response = await fetch("/api/send-security-report", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(reportData),
      });
      
      if (!response.ok) {
        const error = await response.text();
        throw new Error(error || "Failed to send report");
      }
      
      return response.json();
    },
    onSuccess: (data) => {
      setIsReportDialogOpen(false);
      setReportForm({
        email: "",
        reportType: "security-summary",
        includeScans: true,
        includeAlerts: true,
        includeRecommendations: true
      });
      toast({ 
        title: "Report sent successfully!", 
        description: `Security report sent to ${data.recipient}` 
      });
    },
    onError: (error: any) => {
      toast({
        title: "Failed to send report",
        description: error.message || "Please try again later",
        variant: "destructive",
      });
    },
  });

  const notifications = Array.isArray(notificationsData?.notifications) ? notificationsData.notifications : [];
  const settings = notificationsData?.settings || {
    emailNotifications: true,
    securityAlerts: true,
    scanReports: true,
    systemUpdates: false
  };

  const getNotificationIcon = (type: string) => {
    switch (type) {
      case "security": return <Shield className="h-4 w-4 text-red-600" />;
      case "scan": return <Search className="h-4 w-4 text-blue-600" />;
      case "system": return <Settings className="h-4 w-4 text-slate-600" />;
      case "info": return <Info className="h-4 w-4 text-purple-600" />;
      default: return <Bell className="h-4 w-4 text-slate-600" />;
    }
  };

  const getNotificationTypeColor = (type: string) => {
    switch (type) {
      case "security": return "bg-red-100 text-red-800 border-red-200";
      case "scan": return "bg-blue-100 text-blue-800 border-blue-200";
      case "system": return "bg-slate-100 text-slate-800 border-slate-200";
      case "info": return "bg-green-100 text-green-800 border-green-200";
      default: return "bg-slate-100 text-slate-800 border-slate-200";
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case "high": return "bg-red-100 text-red-800 border-red-200";
      case "medium": return "bg-yellow-100 text-yellow-800 border-yellow-200";
      case "low": return "bg-green-100 text-green-800 border-green-200";
      default: return "bg-slate-100 text-slate-800 border-slate-200";
    }
  };

  const filteredNotifications = notifications.filter((notification: any) => {
    const matchesSearch = 
      notification.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      notification.message?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesFilter = filterType === "all" || notification.type === filterType;
    const matchesUnread = !showUnreadOnly || !notification.read;
    return matchesSearch && matchesFilter && matchesUnread;
  });

  const unreadCount = notifications.filter((n: any) => !n.read).length;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white">
            Notifications
          </h1>
          <p className="text-slate-600 dark:text-slate-400">
            Manage your security alerts and system notifications
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Dialog open={isReportDialogOpen} onOpenChange={setIsReportDialogOpen}>
            <DialogTrigger asChild>
              <Button className="flex items-center gap-2">
                <Mail className="h-4 w-4" />
                Send Report
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle>Send Security Report</DialogTitle>
                <DialogDescription>
                  Send a comprehensive security report to an email address
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="report-email">Email Address</Label>
                  <Input
                    id="report-email"
                    type="email"
                    placeholder="recipient@company.com"
                    value={reportForm.email}
                    onChange={(e) => setReportForm({ ...reportForm, email: e.target.value })}
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="report-type">Report Type</Label>
                  <Select value={reportForm.reportType} onValueChange={(value) => setReportForm({ ...reportForm, reportType: value })}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="security-summary">Security Summary</SelectItem>
                      <SelectItem value="detailed-analysis">Detailed Analysis</SelectItem>
                      <SelectItem value="compliance-report">Compliance Report</SelectItem>
                      <SelectItem value="vulnerability-assessment">Vulnerability Assessment</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-3">
                  <Label>Include in Report</Label>
                  <div className="space-y-2">
                    <div className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        id="include-scans"
                        checked={reportForm.includeScans}
                        onChange={(e) => setReportForm({ ...reportForm, includeScans: e.target.checked })}
                        className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                      />
                      <Label htmlFor="include-scans" className="text-sm">Recent scans and results</Label>
                    </div>
                    
                    <div className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        id="include-alerts"
                        checked={reportForm.includeAlerts}
                        onChange={(e) => setReportForm({ ...reportForm, includeAlerts: e.target.checked })}
                        className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                      />
                      <Label htmlFor="include-alerts" className="text-sm">Security alerts and notifications</Label>
                    </div>
                    
                    <div className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        id="include-recommendations"
                        checked={reportForm.includeRecommendations}
                        onChange={(e) => setReportForm({ ...reportForm, includeRecommendations: e.target.checked })}
                        className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                      />
                      <Label htmlFor="include-recommendations" className="text-sm">Security recommendations</Label>
                    </div>
                  </div>
                </div>
                
                <div className="flex justify-end gap-2 pt-4">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setIsReportDialogOpen(false)}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={() => sendReportMutation.mutate(reportForm)}
                    disabled={!reportForm.email || sendReportMutation.isPending}
                  >
                    {sendReportMutation.isPending ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Sending...
                      </>
                    ) : (
                      <>
                        <Send className="mr-2 h-4 w-4" />
                        Send Report
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
          
          <Button
            variant="outline"
            size="sm"
            onClick={() => markAllAsReadMutation.mutate()}
            disabled={unreadCount === 0}
          >
            <CheckCheck className="h-4 w-4 mr-2" />
            Mark All Read
          </Button>
          <Badge variant="secondary" className="text-xs">
            <Bell className="h-3 w-3 mr-1" />
            {unreadCount} Unread
          </Badge>
        </div>
      </div>

      {/* Notification Settings */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Settings className="h-5 w-5 text-blue-600" />
            Notification Settings
          </CardTitle>
          <CardDescription>
            Configure your notification preferences
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="flex items-center justify-between">
              <div className="space-y-1">
                <Label htmlFor="email-notifications">Email Notifications</Label>
                <p className="text-xs text-slate-600">Receive notifications via email</p>
              </div>
              <Switch
                id="email-notifications"
                checked={settings.emailNotifications}
                onCheckedChange={() => {}}
              />
            </div>
            <div className="flex items-center justify-between">
              <div className="space-y-1">
                <Label htmlFor="security-alerts">Security Alerts</Label>
                <p className="text-xs text-slate-600">Critical security notifications</p>
              </div>
              <Switch
                id="security-alerts"
                checked={settings.securityAlerts}
                onCheckedChange={() => {}}
              />
            </div>
            <div className="flex items-center justify-between">
              <div className="space-y-1">
                <Label htmlFor="scan-reports">Scan Reports</Label>
                <p className="text-xs text-slate-600">Weekly scan summaries</p>
              </div>
              <Switch
                id="scan-reports"
                checked={settings.scanReports}
                onCheckedChange={() => {}}
              />
            </div>
            <div className="flex items-center justify-between">
              <div className="space-y-1">
                <Label htmlFor="system-updates">System Updates</Label>
                <p className="text-xs text-slate-600">Platform maintenance notices</p>
              </div>
              <Switch
                id="system-updates"
                checked={settings.systemUpdates}
                onCheckedChange={() => {}}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Search and Filter */}
      <div className="flex items-center gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input
            placeholder="Search notifications..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
        <div className="flex items-center gap-2">
          <Filter className="h-4 w-4 text-slate-500" />
          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            className="px-3 py-2 border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
          >
            <option value="all">All Types</option>
            <option value="security">Security</option>
            <option value="scan">Scans</option>
            <option value="system">System</option>
            <option value="info">Info</option>
          </select>
        </div>
        <div className="flex items-center gap-2">
          <Switch
            id="unread-only"
            checked={showUnreadOnly}
            onCheckedChange={setShowUnreadOnly}
          />
          <Label htmlFor="unread-only" className="text-sm">Unread only</Label>
        </div>
      </div>

      {/* Notifications List */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Bell className="h-5 w-5 text-blue-600" />
            Recent Notifications ({filteredNotifications.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
              <span className="ml-3 text-slate-600">Loading notifications...</span>
            </div>
          ) : filteredNotifications.length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              <Bell className="h-12 w-12 mx-auto mb-4 text-slate-300" />
              <div className="text-lg font-medium mb-2">No Notifications</div>
              <div className="text-sm">
                {searchTerm || filterType !== "all" || showUnreadOnly
                  ? "Try adjusting your search or filter criteria."
                  : "You're all caught up! No new notifications."}
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              {filteredNotifications.map((notification: any) => (
                <div
                  key={notification.id}
                  className={`flex items-start gap-4 p-4 border rounded-lg transition-colors ${
                    notification.read 
                      ? "bg-slate-50 dark:bg-slate-800/50" 
                      : "bg-white dark:bg-slate-700 border-emerald-200 dark:border-emerald-800"
                  }`}
                >
                  <div className="flex-shrink-0 mt-1">
                    {getNotificationIcon(notification.type)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <div className="flex items-center gap-2">
                        <h3 className={`font-medium text-sm ${!notification.read ? "text-slate-900 dark:text-white" : "text-slate-600"}`}>
                          {notification.title}
                        </h3>
                        {!notification.read && (
                          <div className="w-2 h-2 bg-purple-600 rounded-full"></div>
                        )}
                      </div>
                      <div className="flex items-center gap-1">
                        <Badge className={`text-xs border ${getNotificationTypeColor(notification.type)}`}>
                          {notification.type}
                        </Badge>
                        <Badge className={`text-xs border ${getPriorityColor(notification.priority)}`}>
                          {notification.priority}
                        </Badge>
                      </div>
                    </div>
                    <p className="text-sm text-slate-600 dark:text-slate-400 mb-2">
                      {notification.message}
                    </p>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4 text-xs text-slate-500">
                        <span className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {new Date(notification.timestamp).toLocaleString()}
                        </span>
                        {notification.actionUrl && (
                          <Link 
                            href={notification.actionUrl}
                            className="text-purple-600 hover:text-emerald-700 font-medium"
                          >
                            View Details
                          </Link>
                        )}
                      </div>
                      <div className="flex items-center gap-1">
                        {!notification.read && (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => markAsReadMutation.mutate(notification.id)}
                          >
                            <Check className="h-3 w-3" />
                          </Button>
                        )}
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => deleteNotificationMutation.mutate(notification.id)}
                        >
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}